import * as React from 'react';
import { Box, Button, Fade, IconButton, Modal } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Medium } from '../../../types/global';
import ChooseStep from './ChooseStep';
import ContactStep from './ContactStep';
import SentStep from './SentStep';
import { SOFT_PANEL } from '../../../constants/ui';

interface Props {
  open: boolean;
  paymentId: string | null;
  initialEmail?: string | null;
  initialPhone?: string | null;
  showNoReceipt?: boolean;
  onClose: () => void;
  onSent?: () => void;
}

const AUTO_CLOSE_MS = 1600;

export default function GuestReceiptModal({
  open,
  paymentId,
  initialEmail,
  initialPhone,
  showNoReceipt = true,
  onClose,
  onSent,
}: Props) {
  const [mediums, setMediums] = React.useState<Medium[]>([]);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMediums([]);
      setIsSuccess(false);
      setIsSending(false);
    }
  }, [open]);

  const handleSuccess = () => {
    setIsSuccess(true);
    onSent?.();
    window.setTimeout(onClose, AUTO_CLOSE_MS);
  };

  const handleModalClose = (_event: object, reason: string) => {
    if (isSending) return;
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose();
    }
  };

  const showBack = !isSuccess && !isSending && mediums.length > 0;
  const showClose = !isSuccess && !isSending;

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="guest-receipt-modal"
      disableAutoFocus
      closeAfterTransition>
      <Fade in={open} timeout={220}>
        <Box
          role="dialog"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 460 },
            maxHeight: '92vh',
            borderRadius: '20px',
            background: SOFT_PANEL.background,
            boxSizing: 'border-box',
            boxShadow: SOFT_PANEL.shadow,
            outline: 'none',
          }}>
          {showClose && (
            <IconButton
              onClick={onClose}
              aria-label="Close"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 2,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(20, 18, 15, 0.04)',
                },
              }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}

          {showBack && (
            <IconButton
              onClick={() => setMediums([])}
              aria-label="Back"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 2,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(20, 18, 15, 0.04)',
                },
              }}>
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
          )}

          <Box
            sx={{
              position: 'relative',
              px: { xs: 3, sm: 4 },
              pt: 4,
              pb: { xs: 3, sm: 4 },
              boxSizing: 'border-box',
              width: '100%',
            }}>
            {isSuccess ? (
              <SentStep />
            ) : mediums.length > 0 && paymentId ? (
              <ContactStep
                paymentId={paymentId}
                mediums={mediums}
                initialEmail={initialEmail}
                initialPhone={initialPhone}
                onSubmittingChange={setIsSending}
                onSuccess={handleSuccess}
              />
            ) : (
              <>
                <ChooseStep onSelect={setMediums} />
                {showNoReceipt && (
                  <Button
                    variant="link"
                    onClick={onClose}
                    sx={{
                      mt: 2.5,
                      width: '100%',
                      color: 'text.secondary',
                      textTransform: 'none',
                      fontFamily: 'Montserrat, sans-serif',
                      letterSpacing: '0.01em',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'text.primary',
                      },
                      typography: 'subtitle2',
                    }}>
                    No receipt
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
